import PropTypes from "prop-types";
import { Grid, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { Doughnut } from "react-chartjs-2";
import { formatNumberShort } from "../../../helpers/functions/functions";

// Gráfico de aguja (semicírculo) con la efectividad de envío del mes actual
const EffectivenessGauge = ({
  classes,
  darkMode,
  loading,
  windowWidth,
  totalSuccessful,
  totalFailed,
}) => {
  const total = totalSuccessful + totalFailed;
  const effectiveness = total > 0 ? (totalSuccessful * 100) / total : 0;

  return (
    <Grid
      item
      container
      direction={"column"}
      className={classes.totalSmsContainer}
      style={{
        border:
          "2px solid rgba(168, 56, 215, 0.50)",
        boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.10)",
      }}
      sx={{
        flexShrink: 0,
        padding: "12px",
        width: "100%",
        height: "100%",
        marginBottom: 0,
        overflow: "hidden",
        justifyContent: "center",
      }}
    >
      {/* Gauge Section */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Left Stats - Exitosos */}
        <Box
          sx={{
            position: "absolute",
            left: "0",
            top: "0",
            textAlign: "start",
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Ubuntu !important",
              fontWeight: "500",
              fontSize: "18.75px",
              color: "#1BC3DD",
              height: "27px",
            }}
          >
            {formatNumberShort(totalSuccessful)}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Ubuntu !important",
              fontWeight: "400",
              fontSize: "9px",
              color: "#1BC3DD",
            }}
          >
            Exitosos
          </Typography>
        </Box>

        {/* Right Stats - Fallidos */}
        <Box
          sx={{
            position: "absolute",
            right: "0",
            top: "0",
            textAlign: "end",
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Ubuntu !important",
              fontWeight: "500",
              fontSize: "18.75px",
              color: darkMode ? "#D67A63" : "#A838D7",
              height: "27px",
            }}
          >
            {formatNumberShort(totalFailed)}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Ubuntu !important",
              fontWeight: "400",
              fontSize: "9px",
              color: darkMode ? "#D67A63" : "#A838D7",
            }}
          >
            Fallidos
          </Typography>
        </Box>

        {/* Gauge Chart */}
        <Box
          sx={{
            position: "relative",
            height: "74%",
            aspectRatio: "2/1",
            maxWidth: "80%",
            margin: "0 0 15px 0",
          }}
        >
          {/* Background gauge: cyan for success, gray for remaining */}
          <Doughnut
            data={{
              datasets: [
                {
                  data: [effectiveness, 100 - effectiveness],
                  backgroundColor: ["#1BC3DD", "#E2E8F0"],
                  borderWidth: 0,
                  borderRadius: 20,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              rotation: -90,
              circumference: 180,
              cutout: windowWidth > 1300 ? "81%" : "75%",
              plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
                datalabels: { display: false },
              },
            }}
          />
          {/* Overlay: magenta dotted line centered in the gray area */}
          {!loading && (
            <Box
              sx={{
                position: "absolute",
                top: windowWidth > 1300 ? "53%" : "50%",
                left: "50%",
                width: windowWidth > 1500 ? "65%" : "89%",
                height: windowWidth > 1300 ? "100%" : "84%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <Doughnut
                data={{
                  datasets: [
                    {
                      data: (() => {
                        const failedPortion = 100 - effectiveness;
                        const padding = 2; // padding on each side
                        const dottedPortion = Math.max(
                          0,
                          failedPortion - padding * 2,
                        );

                        // Create dotted effect: alternating dots and gaps
                        const dotSize = 6;
                        const gapSize = 2;
                        const segments = [effectiveness + padding]; // Transparent success portion + start padding

                        let remaining = dottedPortion;
                        while (remaining > 0) {
                          // Add dot
                          segments.push(Math.min(dotSize, remaining));
                          remaining -= dotSize;
                          if (remaining > 0) {
                            // Add gap
                            segments.push(Math.min(gapSize, remaining));
                            remaining -= gapSize;
                          }
                        }
                        segments.push(1);
                        return segments;
                      })(),
                      backgroundColor: (() => {
                        const failedPortion = 100 - effectiveness;
                        const padding = 0;
                        const dottedPortion = Math.max(
                          0,
                          failedPortion - padding * 2,
                        );

                        const dotSize = 6;
                        const gapSize = 2;
                        const colors = ["transparent"]; // Success portion + start padding

                        let remaining = dottedPortion;
                        let isDot = true;
                        while (remaining > 0) {
                          colors.push(
                            isDot
                              ? failedPortion == 100
                                ? "transparent"
                                : darkMode
                                  ? "#D67A63"
                                  : "#9C27B0"
                              : "transparent",
                          );
                          remaining -= isDot ? dotSize : gapSize;
                          isDot = !isDot;
                        }
                        colors.push("transparent"); // End padding
                        return colors;
                      })(),
                      borderWidth: 0,
                      borderRadius: 6,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  events: [],
                  maintainAspectRatio: false,
                  rotation: -90,
                  circumference: 180,
                  cutout: windowWidth > 1300 ? "95%" : "94%",
                  plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false },
                    datalabels: { display: false },
                  },
                }}
              />
            </Box>
          )}

          {/* Scale Labels */}
          <Typography
            sx={{
              position: "absolute",
              bottom: windowWidth > 1500 ? "-13%" : "-15%",
              left: windowWidth > 1500 ? "17%" : "3%",
              fontFamily: "Ubuntu !important",
              fontSize: "11px",
              color: darkMode ? "#BBBBBB" : "#637381",
            }}
          >
            0
          </Typography>
          <Typography
            sx={{
              position: "absolute",
              top: windowWidth > 1500 ? "-17px" : "-15px",
              left: "50%",
              transform: "translateX(-50%)",
              fontFamily: "Ubuntu !important",
              fontSize: "11px",
              color: darkMode ? "#BBBBBB" : "#637381",
            }}
          >
            50
          </Typography>
          <Typography
            sx={{
              position: "absolute",
              bottom: windowWidth > 1500 ? "-13%" : "-15%",
              right: windowWidth > 1500 ? "15%" : "2%",
              fontFamily: "Ubuntu !important",
              fontSize: "11px",
              color: darkMode ? "#BBBBBB" : "#637381",
            }}
          >
            100
          </Typography>

          {/* Needle - Triangle pointer (pointing up, rotates from bottom) */}
          <Box
            sx={{
              position: "absolute",
              bottom: "3px",
              left: "50%",
              width: "8px",
              height: windowWidth >= 1500 ? "90px" : "80px",
              transformOrigin: "center bottom",
              transform: `translateX(-50%) rotate(${
                -90 + Math.min(effectiveness, 100) * 1.8
              }deg)`,
              zIndex: 2,
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 8 90"
              preserveAspectRatio="none"
            >
              <polygon
                points="4,0 1,90 7,90"
                fill={darkMode ? "#D67A63" : "#334155"}
              />
            </svg>
          </Box>

          {/* Needle Center Circle */}
          <Box
            sx={{
              position: "absolute",
              bottom: "-2px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "11px",
              height: "11px",
              backgroundColor: darkMode ? "#D67A63" : "#334155",
              borderRadius: "50%",
              zIndex: 3,
            }}
          />

          {/* Effectiveness Text - Centered in gauge */}
          <Box
            sx={{
              position: "absolute",
              bottom:
                windowWidth >= 1500
                  ? "-33%"
                  : windowWidth > 1300
                    ? "-35%"
                    : "-12%",
              left: "50%",
              transform: "translateX(-50%)",
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Ubuntu !important",
                fontWeight: "500",
                lineHeight: "22px",
                fontSize: "22px",
                color: darkMode ? "white" : "#000",
                opacity: 0.5,
              }}
            >
              {effectiveness.toFixed(0)}%
            </Typography>
            <Typography
              sx={{
                fontFamily: "Ubuntu !important",
                fontSize: "8px",
                lineHeight: "10px",
                fontWeight: "500",
                color: darkMode ? "white" : "#000",
                opacity: 0.5,
              }}
            >
              Efectividad
            </Typography>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

EffectivenessGauge.propTypes = {
  classes: PropTypes.object.isRequired,
  darkMode: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  windowWidth: PropTypes.number.isRequired,
  totalSuccessful: PropTypes.number.isRequired,
  totalFailed: PropTypes.number.isRequired,
};

export default EffectivenessGauge;
